import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
}

export function AddCodeDialog({ open, onOpenChange, onAdded }: Props) {
  const { t } = useTranslation();
  const [personalCode, setPersonalCode] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [reactivateId, setReactivateId] = useState<string | null>(null);

  const reset = () => {
    setPersonalCode("");
    setName("");
    setNote("");
    setError("");
    setReactivateId(null);
  };

  const handleSubmit = async () => {
    setError("");
    setReactivateId(null);
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/whitelist`,
        { personalCode, name: name || undefined, note: note || undefined },
        { withCredentials: true }
      );
      reset();
      onOpenChange(false);
      onAdded();
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.code === "DUPLICATE_ACTIVE") {
        setError(t("admin.whitelist.duplicateActive"));
      } else if (data?.code === "DUPLICATE_INACTIVE") {
        setError(t("admin.whitelist.duplicateInactive"));
        setReactivateId(data.existingId);
      } else if (data?.error === "Invalid Estonian personal code") {
        setError(t("admin.whitelist.invalidCode"));
      } else {
        setError(data?.error || "Error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivate = async () => {
    if (!reactivateId) return;
    setSubmitting(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/whitelist/${reactivateId}`,
        { isActive: true, name: name || undefined, note: note || undefined },
        { withCredentials: true }
      );
      reset();
      onOpenChange(false);
      onAdded();
    } catch (err: any) {
      setError(err.response?.data?.error || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("admin.whitelist.addCode")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>{t("admin.whitelist.fieldCode")}</Label>
            <Input
              value={personalCode}
              onChange={(e) => setPersonalCode(e.target.value)}
              placeholder="38305070189"
              className="font-mono"
            />
          </div>
          <div className="space-y-1">
            <Label>{t("admin.whitelist.fieldName")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t("admin.whitelist.fieldNote")}</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20">
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("common.cancel")}
          </Button>
          {reactivateId ? (
            <Button onClick={handleReactivate} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t("admin.whitelist.reactivate")}
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting || !personalCode.trim()}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t("common.save")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
